import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import { LandingView } from '~/components/landing/LandingView';
import { useAnalytics } from '~/hooks/useAnalytics';
import type { PublicLandingPage } from '~/types';

/**
 * Entry point for /$slug.
 * Renders the branded landing page with a real Play CTA. The play app has no
 * player accounts, so there is no auth gate — clicking Play navigates straight
 * into the funnel based on the landing page's target (show → episodes, episode
 * → game).
 */
export const Route = createFileRoute('/$slug/')({
  component: LandingIndexPage,
});

function LandingIndexPage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { trackEvent } = useAnalytics({ slug });

  // Track landing page view
  useEffect(() => {
    trackEvent('view');
  }, [trackEvent]);

  const handlePlay = useCallback(() => {
    if (landingPage.targetType === 'show') {
      router.navigate({ to: '/$slug/episodes', params: { slug } });
    } else {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId: landingPage.targetId },
      });
    }
  }, [router, slug, landingPage]);

  return (
    <LandingView landingPage={landingPage}>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handlePlay}
          className="px-8 py-3 md:px-10 md:py-4 rounded-full text-lg md:text-xl font-bold text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2"
          style={{
            backgroundColor: landingPage.branding.primaryColor,
          }}
        >
          Play
        </button>
      </div>
    </LandingView>
  );
}
